import { Stack, TextField } from "@fluentui/react";
import { Button, Tooltip } from "@fluentui/react-components";
import { Send28Filled, Book24Regular, Wand24Regular, Attach24Regular, Dismiss12Regular } from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";

import { useIsLoggedIn } from "../../authConfig";
import { enhancePromptApi } from "../../api";
import { FileAttachment } from "../../api";
import styles from "./QuestionInput.module.css";
import { SpeechInput } from "./SpeechInput";

const ACCEPTED_FILE_TYPES = ".png,.jpg,.jpeg,.pdf,.txt,.md,.docx,.pptx,.xlsx,.csv,.json,.xml,.html";

interface Props {
    onSend: (question: string, files?: FileAttachment[]) => void;
    disabled: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
    showSpeechInput?: boolean;
    onFilterToggle?: () => void;
    hasActiveFilters?: boolean;
    powerplantId?: string;
    token?: string;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, initQuestion, showSpeechInput, onFilterToggle, hasActiveFilters, powerplantId, token }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const [isEnhancing, setIsEnhancing] = useState<boolean>(false);
    const [enhanceWarning, setEnhanceWarning] = useState<string>("");
    const [attachments, setAttachments] = useState<FileAttachment[]>([]);
    const enhanceWarningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    // Clear warning after a delay
    useEffect(() => {
        return () => {
            if (enhanceWarningTimer.current) clearTimeout(enhanceWarningTimer.current);
        };
    }, []);

    const showWarning = (msg: string) => {
        setEnhanceWarning(msg);
        if (enhanceWarningTimer.current) clearTimeout(enhanceWarningTimer.current);
        enhanceWarningTimer.current = setTimeout(() => setEnhanceWarning(""), 3000);
    };

    const handleEnhancePrompt = async () => {
        if (!question.trim()) {
            showWarning("Type a message to enhance.");
            return;
        }
        if (!powerplantId || !token) return;

        setIsEnhancing(true);
        setEnhanceWarning("");
        try {
            const result = await enhancePromptApi({ powerplant: powerplantId, rawPrompt: question }, token);
            setQuestion(result.enhanced_prompt ?? question);
        } catch {
            showWarning("Prompt enhancement unavailable. Please try again.");
        } finally {
            setIsEnhancing(false);
        }
    };

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question, attachments.length > 0 ? attachments : undefined);

        if (clearOnSend) {
            setQuestion("");
        }
        setAttachments([]);
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (!newValue) {
            setQuestion("");
        } else if (newValue.length <= 2000) {
            setQuestion(newValue);
        }
    };

    const onAttachClick = () => {
        fileInputRef.current?.click();
    };

    const onFilesSelected = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files) return;
        Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result as string;
                const base64 = dataUrl.split(",", 2)[1] ?? "";
                const ext = file.name.includes(".") ? "." + file.name.split(".").pop() : "";
                setAttachments(prev => [
                    ...prev,
                    {
                        encoded_file: base64,
                        file_mime_type: file.type || "application/octet-stream",
                        file_extension: ext,
                        filename: file.name
                    }
                ]);
            };
            reader.readAsDataURL(file);
        });
        ev.target.value = "";
    };

    const removeAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const disableRequiredAccessControl = !useIsLoggedIn();
    const sendQuestionDisabled = disabled || !(question ?? "").trim();

    if (disableRequiredAccessControl) {
        placeholder = "Please login to continue...";
    }

    return (
        <div className={styles.questionInputWrapper}>
            {enhanceWarning && <div className={styles.enhanceWarning}>{enhanceWarning}</div>}
            {attachments.length > 0 && (
                <div className={styles.attachmentsRow}>
                    {attachments.map((att, i) => (
                        <div key={i} className={styles.attachmentChip}>
                            <span className={styles.chipName} title={att.filename}>{att.filename}</span>
                            <button className={styles.chipRemove} onClick={() => removeAttachment(i)} aria-label={`Remove ${att.filename}`}>
                                <Dismiss12Regular />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <Stack horizontal className={styles.questionInputContainer}>
                <TextField
                    className={styles.questionInputTextArea}
                    disabled={disableRequiredAccessControl}
                    placeholder={placeholder}
                    multiline
                    resizable={false}
                    borderless
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                />
                <div className={styles.questionInputButtonsContainer}>
                    <Tooltip content="Attach file" relationship="label">
                        <Button size="large" icon={<Attach24Regular />} disabled={disabled} onClick={onAttachClick} />
                    </Tooltip>
                    {onFilterToggle && (
                        <Tooltip content="Knowledge base" relationship="label">
                            <div className={styles.filterButtonWrapper}>
                                <Button size="large" icon={<Book24Regular />} onClick={onFilterToggle} />
                                {hasActiveFilters && <span className={styles.filterIndicator} />}
                            </div>
                        </Tooltip>
                    )}
                    <Tooltip content="Enhance your prompt" relationship="label">
                        <Button
                            size="large"
                            icon={<Wand24Regular />}
                            disabled={isEnhancing || disabled}
                            onClick={handleEnhancePrompt}
                            className={isEnhancing ? styles.enhanceButtonLoading : undefined}
                        />
                    </Tooltip>
                    <Tooltip content="Submit question" relationship="label">
                        <Button size="large" icon={<Send28Filled primaryFill="rgba(115, 118, 225, 1)" />} disabled={sendQuestionDisabled} onClick={sendQuestion} />
                    </Tooltip>
                </div>
                {showSpeechInput && <SpeechInput updateQuestion={setQuestion} />}
            </Stack>
            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                multiple
                style={{ display: "none" }}
                onChange={onFilesSelected}
            />
        </div>
    );
};

